import { useMemo, useEffect } from "react";
import { GraphConfiguration } from "@/type/common";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  Scatter,
  ScatterChart,
  Sector,
  XAxis,
  YAxis,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Label,
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { toast } from "sonner";
import { cleanDomain } from "@/utils/refactor";

const sanitizeKey = (str: string) => {
  return str
    .toLowerCase() // optional: normalize to lowercase
    .replace(/[^a-z0-9_-]/gi, "-") // replace invalid chars with -
    .replace(/-+/g, "-") // collapse multiple - into one
    .replace(/^-|-$/g, ""); // remove leading/trailing -
};

// Format Y-axis values with appropriate suffixes (k, m) for large numbers
const formatYAxisValue = (value: any) => {
  if (typeof value !== "number") return value;

  if (value >= 1000000) {
    return `${(value / 1000000).toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}m`;
  } else if (value >= 1000) {
    return `${(value / 1000).toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}k`;
  }

  return value;
};

const processChartData = (data: any[], _config: GraphConfiguration) => {
  if (!data || data.length === 0) return [];

  return data.map((item) => {
    const newItem: any = {};

    for (const key in item) {
      const newKey = sanitizeKey(
        key.includes("|") ? key.replace(/\|/g, "___") : key
      );

      // For double groupby: extract value for Recharts, but preserve full object for domain access
      if (typeof item[key] === "object" && item[key].value !== undefined) {
        // Store the value for Recharts to render
        newItem[newKey] = item[key].value;
        // Also store the full object with __domain under a special key
        newItem[`${newKey}___meta`] = item[key];
      } else {
        newItem[newKey] = item[key];
      }
    }

    return newItem;
  });
};

const generateChartConfig = (
  _data: any[],
  config: GraphConfiguration,
  measures: any[]
): ChartConfig => {
  const chartConfig: ChartConfig = {};

  if (
    ["pie", "donut"].includes(config.graph_options.chartType) &&
    config.data_source.groupBy.length > 0
  ) {
    const groupBy = config.data_source.groupBy[0];

    const field = groupBy.field;
    const existingField = config.fields?.find((f) => f.field === field);

    if (existingField && existingField.type === "selection") {
      existingField.selection.forEach((item: any) => {
        chartConfig[item.value] = {
          label: item.label,
          color:
            (config.graph_options.colors &&
              config.graph_options.colors[
                config.data_source.groupBy.indexOf(groupBy) %
                  config.graph_options.colors.length
              ]) ||
            `hsl(var(--chart-${
              (config.data_source.groupBy.indexOf(groupBy) % 10) + 1
            }))`,
        };
      });
      return chartConfig;
    }
  }

  // Add measure configurations
  measures.forEach((measure, index) => {
    const field = sanitizeKey(measure.field);
    const existingField = config.fields?.find((f) => f.field === field);
    const displayName = measure?.displayName || existingField?.name || field;

    chartConfig[field] = {
      label: displayName,
      color:
        (config.graph_options.colors &&
          config.graph_options.colors[
            index % config.graph_options.colors.length
          ]) ||
        `hsl(var(--chart-${(index % 10) + 1}))`,
    };
  });

  return chartConfig;
};

// Utility function to get legend props based on options and layout
const getLegendProps = (
  options: GraphConfiguration["graph_options"],
  layout?: "centric"
) => {
  if (!options.legend?.show) {
    return null;
  }

  return {
    layout:
      layout ||
      (options.legend.position === "left" || options.legend.position === "right"
        ? "vertical"
        : "horizontal"),
    align: "center",
    verticalAlign: options.legend.position === "top" ? "top" : "bottom",
  };
};

// Utility function to get X-axis props
const getXAxisProps = (options: GraphConfiguration["graph_options"]) => {
  return {
    dataKey: "key",
    tickLine: options.xAxis.showAxis,
    axisLine: options.xAxis.showAxis,
    tickMargin: 8,
    angle: options.xAxis.tickRotation,
    tickFormatter: (value: any) =>
      typeof value === "string" && value.length > 10
        ? `${value.slice(0, 10)}...`
        : value,
  };
};

// Utility function to get Y-axis props
const getYAxisProps = (
  options: GraphConfiguration["graph_options"],
  orientation: "left" | "right" = "left",
  additionalProps: Record<string, any> = {}
) => {
  const axisOptions =
    orientation === "left" ? options.yAxis.left : options.yAxis.right;

  return {
    yAxisId: orientation,
    orientation: orientation,
    tickLine: axisOptions.showAxis,
    axisLine: axisOptions.showAxis,
    hide: !axisOptions.showAxis,
    domain: [
      axisOptions.min !== null ? axisOptions.min : "auto",
      axisOptions.max !== null ? axisOptions.max : "auto",
    ],
    tickFormatter: formatYAxisValue,
    ...additionalProps,
  };
};

// Utility function to get ReferenceLine props
const getReferenceLineProps = (
  threshold: GraphConfiguration["graph_options"]["thresholds"][0],
  additionalProps: Record<string, any> = {}
) => {
  return {
    y: threshold.y,
    yAxisId: threshold.axis,
    stroke: threshold.color,
    strokeDasharray: threshold.dash ? "3 3" : "0",
    label: threshold.label,
    ...additionalProps,
  };
};

// Utility function to get ReferenceArea props
const getReferenceAreaProps = (
  area: GraphConfiguration["graph_options"]["referenceAreas"][0],
  additionalProps: Record<string, any> = {}
) => {
  return {
    y1: area.y1,
    y2: area.y2,
    yAxisId: area.axis,
    fill: area.color,
    fillOpacity: 0.2,
    label: area.label,
    ...additionalProps,
  };
};

// Component to render a bar chart
const BarChartComponent = ({
  data,
  config,
  chartConfig,
  measures,
  navigate,
}: {
  data: any[];
  config: GraphConfiguration;
  chartConfig: ChartConfig;
  measures: any[];
  navigate: (target: any) => void;
}) => {
  const { graph_options: options } = config;
  const stacked = options.stacked;
  const overlay = options.barOverlay || false;
  const legendProps: any = getLegendProps(options);
  const xAxisProps: any = getXAxisProps(options);
  const leftYAxisProps: any = getYAxisProps(options, "left");
  const rightYAxisProps: any = getYAxisProps(options, "right");

  const graphConfig: any = {};

  config.graph_options.measures.forEach((measure) => {
    graphConfig[measure.field] = {
      label: measure.displayName || measure.field,
    };
  });

  // Calculate opacity for overlay mode based on number of measures
  const overlayOpacity = overlay ? Math.max(0.4, 1 / measures.length) : 1;

  return (
    <ChartContainer className="h-full w-full" config={chartConfig}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          onClick={(e) => {
            // This fires when clicking on the chart background (not on a bar)
            if (e && e.activePayload && e.activePayload.length > 0) {
              const payload = e.activePayload[0];
              const target = {
                type: "navigate",
                domain: payload.payload["__domain"],
              };
              navigate(target);
            }
          }}
          accessibilityLayer
          data={data}
          barGap={overlay ? "-60%" : 4}
          barCategoryGap={overlay ? "20%" : "20%"}
          margin={{
            top: 5,
            right:
              options.yAxis.right.showAxis && !options.yAxis.right.title
                ? -10
                : 5,
            left:
              options.yAxis.left.showAxis && !options.yAxis.left.title
                ? -10
                : 5,
            bottom: 5,
          }}
        >
          {options.showGrid && <CartesianGrid vertical={true} />}
          <XAxis {...xAxisProps} />
          <YAxis {...leftYAxisProps}>
            {options.yAxis.left.title && (
              <Label
                value={options.yAxis.left.title}
                angle={-90}
                style={{ textAnchor: "middle" }}
                position={"insideLeft"}
              />
            )}
          </YAxis>
          <YAxis {...rightYAxisProps}>
            {options.yAxis.left.title && (
              <Label
                value={options.yAxis.right.title}
                angle={-90}
                style={{ textAnchor: "middle" }}
                position={"insideRight"}
              />
            )}
          </YAxis>
          {options.thresholds.map((threshold, idx) => (
            <ReferenceLine
              {...getReferenceLineProps(threshold)}
              key={`threshold-${idx}`}
            />
          ))}
          {options.referenceAreas.map((area, idx) => (
            <ReferenceArea
              {...getReferenceAreaProps(area)}
              key={`reference-area-${idx}`}
            />
          ))}
          <ChartTooltip content={<ChartTooltipContent indicator="dashed" />} />
          {legendProps && (
            <ChartLegend {...legendProps} content={<ChartLegendContent />} />
          )}

          {measures.map((measure, index) => (
            <Bar
              animationDuration={options.animation ? 400 : 0}
              onClick={(data, index, e) => {
                // Prevent the event from bubbling to the BarChart onClick
                if (e && e.stopPropagation) {
                  e.stopPropagation();
                }

                const payload = data.payload;
                // For double groupby, domain is stored in the metadata object
                const metaKey = `${measure.field}___meta`;
                const domain = payload[metaKey]?.__domain || payload["__domain"];
                
                if (domain) {
                  const target = {
                    type: "navigate",
                    domain: domain,
                  };
                  navigate(target);
                }
              }}
              className="cursor-pointer"
              key={`bar-${measure.field}`}
              dataKey={measure.field}
              name={measure.displayName || measure.field}
              fill={`var(--color-${measure.field})`}
              fillOpacity={overlayOpacity}
              radius={
                index === options.measures.length - 1 ||
                !stacked ||
                (stacked &&
                  options.measures.filter((m) => m.yAxis === measure.yAxis)
                    .length === 1)
                  ? [4, 4, 0, 0]
                  : [0, 0, 0, 0]
              }
              stackId={stacked && !overlay ? "stack" : undefined}
              yAxisId={measure.yAxis === "right" ? "right" : "left"}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

// Component to render a line chart
const LineChartComponent = ({
  data,
  config,
  chartConfig,
  measures,
  navigate,
}: {
  data: any[];
  config: GraphConfiguration;
  chartConfig: ChartConfig;
  measures: any[];
  navigate: (target: any) => void;
}) => {
  const { graph_options: options } = config;
  const legendProps: any = getLegendProps(options);
  const xAxisProps: any = getXAxisProps(options);
  const leftYAxisProps: any = getYAxisProps(options, "left");
  const rightYAxisProps: any = getYAxisProps(options, "right");

  return (
    <ChartContainer className="h-full w-full" config={chartConfig}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          onClick={(e) => {
            if (e && e.activePayload && e.activePayload.length > 0) {
              const payload = e.activePayload[0];
              const target = {
                type: "navigate",
                domain: payload.payload["__domain"],
              };
              navigate(target);
            }
          }}
          data={data}
          margin={{
            top: 5,
            right:
              options.yAxis.right.showAxis && !options.yAxis.right.title
                ? -10
                : 5,
            left:
              options.yAxis.left.showAxis && !options.yAxis.left.title
                ? -10
                : 5,
            bottom: 5,
          }}
        >
          {options.showGrid && <CartesianGrid vertical={true} />}
          <XAxis {...xAxisProps} />
          <YAxis {...leftYAxisProps}>
            {options.yAxis.left.title && (
              <Label
                value={options.yAxis.left.title}
                angle={-90}
                style={{ textAnchor: "middle" }}
                position={"insideLeft"}
              />
            )}
          </YAxis>
          <YAxis {...rightYAxisProps}>
            {options.yAxis.left.title && (
              <Label
                value={options.yAxis.right.title}
                angle={-90}
                style={{ textAnchor: "middle" }}
                position={"insideRight"}
              />
            )}
          </YAxis>
          {options.thresholds.map((threshold, idx) => (
            <ReferenceLine
              {...getReferenceLineProps(threshold)}
              key={`threshold-${idx}`}
            />
          ))}
          {options.referenceAreas.map((area, idx) => (
            <ReferenceArea
              {...getReferenceAreaProps(area)}
              key={`reference-area-${idx}`}
            />
          ))}
          <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
          {legendProps && (
            <ChartLegend {...legendProps} content={<ChartLegendContent />} />
          )}

          {measures.map((measure, _index) => (
            <Line
              animationDuration={options.animation ? 400 : 0}
              key={`line-${measure.field}`}
              type="monotone"
              dataKey={measure.field}
              name={measure.displayName || measure.field}
              stroke={`var(--color-${measure.field})`}
              yAxisId={measure.yAxis === "right" ? "right" : "left"}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

// Component to render an area chart
const AreaChartComponent = ({
  data,
  config,
  chartConfig,
  measures,
  navigate,
}: {
  data: any[];
  config: GraphConfiguration;
  chartConfig: ChartConfig;
  measures: any[];
  navigate: (target: any) => void;
}) => {
  const { graph_options: options } = config;
  const stacked = options.stacked;
  const legendProps: any = getLegendProps(options);
  const xAxisProps: any = getXAxisProps(options);
  const leftYAxisProps: any = getYAxisProps(options, "left");
  const rightYAxisProps: any = getYAxisProps(options, "right");

  return (
    <ChartContainer className="h-full w-full" config={chartConfig}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          accessibilityLayer
          data={data}
          margin={{
            top: 5,
            right: 5,
            left: options.yAxis.left.showAxis ? -15 : 5,
            bottom: 5,
          }}
          onClick={(e) => {
            if (e && e.activePayload && e.activePayload.length > 0) {
              const payload = e.activePayload[0];
              const target = {
                type: "navigate",
                domain: payload.payload["__domain"],
              };
              navigate(target);
            }
          }}
        >
          {options.showGrid && <CartesianGrid vertical={true} />}
          <XAxis {...xAxisProps} />
          <YAxis {...leftYAxisProps} />
          <YAxis {...rightYAxisProps} />
          {options.thresholds.map((threshold, idx) => (
            <ReferenceLine
              {...getReferenceLineProps(threshold)}
              key={`threshold-${idx}`}
            />
          ))}
          {options.referenceAreas.map((area, idx) => (
            <ReferenceArea
              {...getReferenceAreaProps(area)}
              key={`reference-area-${idx}`}
            />
          ))}
          <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
          {legendProps && (
            <ChartLegend {...legendProps} content={<ChartLegendContent />} />
          )}

          {measures.map((measure) => (
            <Area
              animationDuration={options.animation ? 400 : 0}
              key={`area-${measure.field}`}
              type="monotone"
              dataKey={measure.field}
              name={measure.displayName || measure.field}
              stroke={`var(--color-${measure.field})`}
              fill={`var(--color-${measure.field})`}
              fillOpacity={0.4}
              stackId={stacked ? "stack" : undefined}
              yAxisId={measure.yAxis === "right" ? "right" : "left"}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

// Custom label renderer for pie/donut charts
const renderPieLabel = (labelConfig: any) => (props: any) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent, name, value } = props;
  
  // Only show label if percentage is significant enough (> 3%)
  if (percent < 0.03) return null;
  
  const RADIAN = Math.PI / 180;
  // Position label between inner and outer radius
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  // Format label text based on configuration
  let labelText = "";
  switch (labelConfig.type) {
    case "percentage":
      labelText = `${(percent * 100).toFixed(1)}%`;
      break;
    case "value":
      labelText = `${value}`;
      break;
    case "both":
      labelText = `${(percent * 100).toFixed(1)}% (${value})`;
      break;
    case "name":
      labelText = name;
      break;
    default:
      labelText = `${(percent * 100).toFixed(1)}%`;
  }

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      className="font-semibold text-xs"
      style={{ 
        pointerEvents: "none",
        textShadow: "0 0 3px rgba(0,0,0,0.8), 0 0 2px rgba(0,0,0,0.8)"
      }}
    >
      {labelText}
    </text>
  );
};

// Active shape renderer for pie/donut hover effect
const renderActiveShape = (props: any) => {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
  } = props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius * 1.08}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        stroke="hsl(var(--background))"
        strokeWidth={2}
      />
    </g>
  );
};

// Component to render a pie chart
const PieChartComponent = ({
  data,
  config,
  chartConfig,
  navigate,
}: {
  data: any[];
  config: GraphConfiguration;
  chartConfig: ChartConfig;
  navigate: (target: any) => void;
}) => {
  const { graph_options: options } = config;
  // Make sure we have a measure to display
  let measureField = "";
  if (config.graph_options.measures.length > 0) {
    measureField = config.graph_options.measures[0]?.field || "";
  }
  const legendProps: any = getLegendProps(options, "centric");

  // Default pieLabels config for backward compatibility
  const pieLabelsConfig = options.pieLabels || { show: true, type: "percentage" };

  return (
    <ChartContainer className="h-full w-full" config={chartConfig}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
            <ChartTooltip content={<ChartTooltipContent hideLabel />} />
            <Pie
              animationDuration={options.animation ? 400 : 0}
              data={data.slice(0, 32)}
              dataKey={measureField}
              nameKey="key"
              cx="50%"
              cy="50%"
              className="cursor-pointer"
              outerRadius={"60%"}
              innerRadius={config.graph_options.chartType === "donut" ? "40%" : 0}
              label={pieLabelsConfig.show ? renderPieLabel(pieLabelsConfig) : false}
              labelLine={false}
              activeShape={renderActiveShape}
              onClick={(e) => {
                const target = {
                  type: "navigate",
                  domain: e.payload["__domain"],
                };
                navigate(target);
              }}
            >
              {data.slice(0, 32).map((_entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    config.graph_options.colors?.[
                      index % (config.graph_options.colors?.length || 1)
                    ] || `hsl(var(--chart-${(index % 10) + 1}))`
                  }
                />
              ))}
            </Pie>
            {legendProps && (
              <Legend
                {...legendProps}
                onClick={(e: any) => {
                  // Find the data item that matches the clicked legend
                  const clickedItem = data.find((item) => item.key === e.value);
                  if (clickedItem && clickedItem["__domain"]) {
                    const target = {
                      type: "navigate",
                      domain: clickedItem["__domain"],
                    };
                    navigate(target);
                  }
                }}
                wrapperStyle={{ cursor: "pointer" }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
    </ChartContainer>
  );
};

// Component to render a scatter chart
const ScatterChartComponent = ({
  data,
  config,
  chartConfig,
  navigate,
}: {
  data: any[];
  config: GraphConfiguration;
  chartConfig: ChartConfig;
  navigate: (target: any) => void;
}) => {
  // Scatter charts typically need at least 2 measures
  const { graph_options: options } = config;
  const legendProps: any = getLegendProps(options);
  const leftYAxisProps: any = getYAxisProps(options, "left", {
    type: "number",
    dataKey: options.measures[1]?.field,
    name: options.measures[1]?.displayName || options.measures[1]?.field,
  });

  if (options.measures.length < 2) {
    return <div>Scatter charts require at least 2 measures</div>;
  }

  return (
    <ChartContainer className="h-full w-full" config={chartConfig}>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart
          accessibilityLayer
          margin={{
            top: 5,
            right: 5,
            left: options.yAxis.left.showAxis ? -15 : 5,
            bottom: 5,
          }}
        >
          {options.showGrid && <CartesianGrid vertical={true} />}
          <XAxis
            type="number"
            dataKey={options.measures[0].field}
            name={options.measures[0].displayName || options.measures[0].field}
            tickLine={options.xAxis.showAxis}
            axisLine={options.xAxis.showAxis}
            domain={["auto", "auto"]}
          />
          <YAxis {...leftYAxisProps} />
          <ChartTooltip content={<ChartTooltipContent />} />
          {legendProps && (
            <ChartLegend {...legendProps} content={<ChartLegendContent />} />
          )}

          <Scatter
            animationDuration={options.animation ? 400 : 0}
            name={options.measures[1].displayName || options.measures[1].field}
            data={data}
            fill={config.graph_options.colors?.[0] || `hsl(var(--chart-1))`}
            yAxisId={"left"}
          />
        </ScatterChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

// Component to render a composed chart
const ComposedChartComponent = ({
  data,
  config,
  chartConfig,
  measures,
  navigate,
}: {
  data: any[];
  config: GraphConfiguration;
  chartConfig: ChartConfig;
  measures: any[];
  navigate: (target: any) => void;
}) => {
  const { graph_options: options } = config;
  const stacked = options.stacked;
  const legendProps: any = getLegendProps(options);
  const xAxisProps: any = getXAxisProps(options);
  const leftYAxisProps: any = getYAxisProps(options, "left");
  const rightYAxisProps: any = getYAxisProps(options, "right");

  return (
    <ChartContainer className="h-full w-full" config={chartConfig}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          accessibilityLayer
          data={data}
          margin={{
            top: 5,
            right: 5,
            left: options.yAxis.left.showAxis ? -15 : 5,
            bottom: 5,
          }}
          onClick={(e) => {
            if (e && e.activePayload && e.activePayload.length > 0) {
              const payload = e.activePayload[0];
              const target = {
                type: "navigate",
                domain: payload.payload["__domain"],
              };
              navigate(target);
            }
          }}
        >
          {options.showGrid && <CartesianGrid vertical={true} />}
          <XAxis {...xAxisProps} />
          <YAxis {...leftYAxisProps} />
          <YAxis {...rightYAxisProps} />
          <ChartTooltip content={<ChartTooltipContent />} />
          {legendProps && (
            <ChartLegend {...legendProps} content={<ChartLegendContent />} />
          )}

          {measures.map((measure, index) => {
            const chartType = measure.type || "bar";

            if (chartType === "bar") {
              return (
                <Bar
                  animationDuration={options.animation ? 400 : 0}
                  key={`bar-${measure.field}`}
                  dataKey={measure.field}
                  name={measure.displayName || measure.field}
                  fill={`var(--color-${measure.field})`}
                  radius={4}
                  stackId={stacked ? "stack" : undefined}
                  yAxisId={measure.yAxis === "right" ? "right" : "left"}
                  className="cursor-pointer"
                />
              );
            } else if (chartType === "line") {
              return (
                <Line
                  animationDuration={options.animation ? 400 : 0}
                  key={`line-${measure.field}`}
                  type="monotone"
                  dataKey={measure.field}
                  name={measure.displayName || measure.field}
                  stroke={`var(--color-${measure.field})`}
                  activeDot={{ r: 6 }}
                  yAxisId={measure.yAxis === "right" ? "right" : "left"}
                />
              );
            } else if (chartType === "area") {
              return (
                <Area
                  animationDuration={options.animation ? 400 : 0}
                  key={`area-${measure.field}`}
                  type="monotone"
                  dataKey={measure.field}
                  name={measure.displayName || measure.field}
                  stroke={`var(--color-${measure.field})`}
                  fill={`var(--color-${measure.field})`}
                  fillOpacity={0.4}
                  stackId={stacked ? "stack" : undefined}
                  yAxisId={measure.yAxis === "right" ? "right" : "left"}
                  className="cursor-pointer"
                />
              );
            }

            return null;
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

// Main component to render the graph
const RenderGraph = ({
  config,
  data,
  models,
}: {
  config: GraphConfiguration;
  data: any[];
  models: any[];
}) => {
  // Process data for chart rendering
  const processedData = useMemo(
    () => processChartData(data, config),
    [data, config]
  );

  const completeMeasures = useMemo(() => {
    if (!config.data_source.groupBy || config.data_source.groupBy.length <= 1)
      return config.graph_options.measures;

    const lines = new Set<string>();

    data.forEach((line: any) => {
      Object.keys(line).forEach((key) => {
        lines.add(key);
      });
    });
    lines.delete("key");
    lines.delete("__domain");

    const linesArray = Array.from(lines);

    return linesArray.map((line) => {
      const measure = config.graph_options.measures.find(
        (m) => m.field === line.split("|")[0]
      );
      return {
        ...measure,
        displayName: `${measure?.displayName} ${line.split("|")[1]}`,
        field: sanitizeKey(line.replace("|", "___")),
      };
    });
  }, [config, data]);

  // Generate chart config for shadcn
  const chartConfig = useMemo(
    () => generateChartConfig(data, config, completeMeasures),
    [completeMeasures]
  );

  // Return early if no data
  if (!processedData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{config.title}</CardTitle>
          {config.description && (
            <CardDescription>{config.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">No data available</div>
        </CardContent>
      </Card>
    );
  }

  const navigate = (target: any) => {
    if (config.data_source.sqlRequest) {
      toast.info("You can't navigate on a graph with a SQL request");
      return;
    }
    if (window.location.href.includes("/edit")) {
      toast.info("You can't navigate on a graph in edit mode");
      return;
    }
    target.model = config.model;
    target.name = models.find((m) => m.value === config.model)?.label;
    if (config.data_source.domain) {
      target.domain = [...target.domain, ...config.data_source.domain];
    }
    target.domain = cleanDomain(target.domain);
    window.parent.postMessage(target, "*");
  };

  return (
    <div className="h-full w-full">
      {/* Render different chart types based on configuration */}
      {config.graph_options.chartType === "bar" && (
        <BarChartComponent
          data={processedData}
          config={config}
          chartConfig={chartConfig}
          measures={completeMeasures}
          navigate={navigate}
        />
      )}

      {config.graph_options.chartType === "line" && (
        <LineChartComponent
          data={processedData}
          config={config}
          chartConfig={chartConfig}
          measures={completeMeasures}
          navigate={navigate}
        />
      )}

      {config.graph_options.chartType === "area" && (
        <AreaChartComponent
          data={processedData}
          config={config}
          chartConfig={chartConfig}
          measures={completeMeasures}
          navigate={navigate}
        />
      )}

      {(config.graph_options.chartType === "pie" ||
        config.graph_options.chartType === "donut") && (
        <PieChartComponent
          data={processedData}
          config={config}
          chartConfig={chartConfig}
          navigate={navigate}
        />
      )}

      {config.graph_options.chartType === "scatter" && (
        <ScatterChartComponent
          data={processedData}
          config={config}
          chartConfig={chartConfig}
          navigate={navigate}
        />
      )}

      {config.graph_options.chartType === "composed" && (
        <ComposedChartComponent
          data={processedData}
          config={config}
          chartConfig={chartConfig}
          measures={completeMeasures}
          navigate={navigate}
        />
      )}
    </div>
  );
};

export default RenderGraph;
